/* eslint-disable max-lines */
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { from, Observable } from 'rxjs';
import { EXCEPTION_CATEGORY_ENUM } from '../../../../constants/utils';
import {
  LoginOAuthParams,
  SwapAuthForAccessTokens,
  SwapAuthForAccessTokensParams
} from '../../../../models/login';
import { IExceptionMetadata } from '../../../../models/utils';
import { LoginRememberMeUtilsService } from '../../../../utils/login';
import { ExceptionTrackingService } from '../../../utils';
const fileName = 'SwapAuthForAccessTokenService';
@Injectable({
  providedIn: 'root'
})
export class SwapAccessTokenService {
  accessTokenHeader = new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded'
  });

  constructor(
    @Inject(PLATFORM_ID) private platformId: NonNullable<unknown>,
    private http: HttpClient,
    private utils: LoginRememberMeUtilsService,
    private exceptionTracking: ExceptionTrackingService
  ) {}

  async swapAuthForAccessTokens(
    swapAuthForAccessTokensParams: SwapAuthForAccessTokensParams,
    mfaClientIdForSwap?: boolean
  ): Promise<SwapAuthForAccessTokens> {
    if (
      this.swapAuthForAccessTokensParamsValCheck(swapAuthForAccessTokensParams)
    ) {
      return this.errorResponse('ERROR_MSG_223');
    }
    try {
      return await this.responseOnPingSwapAuthForAccessUrl(
        swapAuthForAccessTokensParams,
        mfaClientIdForSwap
      );
    } catch (exception: unknown) {
      return this.exceptionHandling(exception);
    }
  }

  swapAuthCodeForToken(
    loginOAuthParams: LoginOAuthParams,
    authorizeResponse?: string
  ): Observable<SwapAuthForAccessTokens> {
    const swapAccessTokenObject: SwapAuthForAccessTokensParams = {
      authorizationCode: authorizeResponse,
      oauthConfig: loginOAuthParams.oauthConfig,
      pkcCodeGenerated: loginOAuthParams.pkceCode
    };
    return from(this.swapAuthForAccessTokens(swapAccessTokenObject));
  }

  async responseOnPingSwapAuthForAccessUrl(
    swapAuthForAccessTokensParams: SwapAuthForAccessTokensParams,
    mfaClientIdForSwap?: boolean
  ): Promise<SwapAuthForAccessTokens> {
    const body = this.bodyValueWithParams(
      swapAuthForAccessTokensParams,
      mfaClientIdForSwap
    );
    const pingSwapAuthForAccessUrl = this.utils.getPingServiceUrl(
      swapAuthForAccessTokensParams.oauthConfig,
      undefined,
      undefined
    ).swapAuthForAccessTokensUrl;
    if (isPlatformBrowser(this.platformId) && pingSwapAuthForAccessUrl) {
      return await this.returnResponseData(
        pingSwapAuthForAccessUrl,
        body,
        swapAuthForAccessTokensParams
      );
    } else {
      return this.errorResponse('ERROR_MSG_224');
    }
  }

  errorResponse(errCode: string): {
    errors: {
      code: string;
    }[];
  } {
    return {
      errors: [
        {
          code: errCode
        }
      ]
    };
  }

  bodyValueWithParams(
    swapAuthForAccessTokensParams: SwapAuthForAccessTokensParams,
    mfaClientIdForSwap?: boolean
  ): string {
    if (mfaClientIdForSwap) {
      return `${swapAuthForAccessTokensParams.oauthConfig.GRANT_TYPE}=${swapAuthForAccessTokensParams.oauthConfig.AUTHORIZATION_CODE}&${swapAuthForAccessTokensParams.oauthConfig.CODE}=${swapAuthForAccessTokensParams.authorizationCode}&${swapAuthForAccessTokensParams.oauthConfig.CODE_VERIFIER}=${swapAuthForAccessTokensParams.pkcCodeGenerated.codeVerifier}&${swapAuthForAccessTokensParams.oauthConfig.CLIENT_ID}=${swapAuthForAccessTokensParams.oauthConfig.MFA_CLIENT_ID_VALUE}`;
    } else {
      return `${swapAuthForAccessTokensParams.oauthConfig.GRANT_TYPE}=${swapAuthForAccessTokensParams.oauthConfig.AUTHORIZATION_CODE}&${swapAuthForAccessTokensParams.oauthConfig.CODE}=${swapAuthForAccessTokensParams.authorizationCode}&${swapAuthForAccessTokensParams.oauthConfig.CODE_VERIFIER}=${swapAuthForAccessTokensParams.pkcCodeGenerated.codeVerifier}&${swapAuthForAccessTokensParams.oauthConfig.CLIENT_ID}=${swapAuthForAccessTokensParams.oauthConfig.CLIENT_ID_VALUE}`;
    }
  }

  exceptionHandling(exception: unknown): SwapAuthForAccessTokens {
    const exceptionMetadata: IExceptionMetadata = {
      fileName,
      methodName: 'swapAuthForAccessTokens',
      exceptionCategory: EXCEPTION_CATEGORY_ENUM.JS_CATCH_BLOCK,
      exceptionMessage:
        'Exception caught while calling Swap Authorization Token for Access Token'
    };
    const exceptionStr = exception as string;
    this.exceptionTracking.track(exceptionStr, exceptionMetadata);
    return exception as SwapAuthForAccessTokens;
  }

  async returnResponseData(
    pingSwapAuthForAccessUrl: string,
    body: string,
    swapAuthForAccessTokensParams: SwapAuthForAccessTokensParams
  ): Promise<SwapAuthForAccessTokens> {
    const data: SwapAuthForAccessTokens = await this.http
      .post<SwapAuthForAccessTokens>(`${pingSwapAuthForAccessUrl}`, body, {
        headers: this.accessTokenHeader,
        responseType: 'json'
      })
      .toPromise()
      .catch((error: HttpErrorResponse) => {
        if (
          this.swapAuthForAccessTokensParamsErrorCodeFilter(
            swapAuthForAccessTokensParams,
            error
          ) ||
          error.error.error
        ) {
          return this.errorResponse('ERROR_MSG_128');
        }
        return error;
      });
    return data;
  }

  swapAuthForAccessTokensParamsErrorCodeFilter(
    swapAuthForAccessTokensParams: SwapAuthForAccessTokensParams,
    error: HttpErrorResponse
  ): boolean | undefined {
    return (
      swapAuthForAccessTokensParams.oauthConfig &&
      swapAuthForAccessTokensParams.oauthConfig.EAM_OUTAGE_ERROR_CODE_IGNORE &&
      swapAuthForAccessTokensParams.oauthConfig.EAM_OUTAGE_ERROR_CODE_IGNORE.filter(
        (code) => {
          return error?.status === parseInt(code);
        }
      ).length < 1
    );
  }

  swapAuthForAccessTokensParamsValCheck(
    swapAuthForAccessTokensParams: SwapAuthForAccessTokensParams
  ): boolean {
    return (
      !swapAuthForAccessTokensParams.authorizationCode ||
      !swapAuthForAccessTokensParams.oauthConfig ||
      !swapAuthForAccessTokensParams.pkcCodeGenerated ||
      !swapAuthForAccessTokensParams.pkcCodeGenerated.codeVerifier
    );
  }
}
